import React, { useEffect, useRef, useState } from "react";
import QRCode from "react-qr-code";
import Icon from "../Shared/Icon";
import { KAVACH_COLORS } from "./KavachData";
import {
  getUserInfoByRetailerCode,
  onboardRetailer,
} from "../../Actions/Kavach";
import { variables } from "../../environments";

const STATUS = { LOADING: "loading", READY: "ready", ERROR: "error" };

const downloadQrAsPng = (svgEl, fileName) => {
  const svgData = new XMLSerializer().serializeToString(svgEl);
  const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
  const svgUrl = URL.createObjectURL(svgBlob);
  const img = new Image();

  img.onload = () => {
    const padding = 20;
    const canvas = document.createElement("canvas");
    canvas.width = img.width + padding * 2;
    canvas.height = img.height + padding * 2;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, padding, padding);
    URL.revokeObjectURL(svgUrl);

    const link = document.createElement("a");
    link.download = fileName;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  img.src = svgUrl;
};

const KavachGenerateQrModal = ({
  open,
  retailerCode,
  knownCbUserId,
  shopName: shopNameProp,
  onClose,
}) => {
  const [status, setStatus] = useState(STATUS.LOADING);
  const [qrUrl, setQrUrl] = useState("");
  const [shopName, setShopName] = useState(shopNameProp || "");
  const qrSvgWrapRef = useRef(null);

  const handleDownload = () => {
    const svgEl = qrSvgWrapRef.current?.querySelector("svg");
    if (svgEl) downloadQrAsPng(svgEl, `${retailerCode || "kavach"}-qr.png`);
  };

  useEffect(() => {
    if (!open || !retailerCode) return;

    let cancelled = false;
    setStatus(STATUS.LOADING);
    setQrUrl("");
    setShopName(shopNameProp || "");

    const buildQrUrl = (cbUserId) =>
      `${variables.LifelineUrl}/?retailer=${retailerCode}-${cbUserId}`;

    (async () => {
      try {
        let resolvedCbUserId = knownCbUserId || null;

        if (!resolvedCbUserId || !shopNameProp) {
          const infoRes = await getUserInfoByRetailerCode(retailerCode);
          resolvedCbUserId =
            resolvedCbUserId || infoRes?.data?.data?.cbUserId || null;
          if (!cancelled && infoRes?.data?.data?.shopName) {
            setShopName(infoRes.data.data.shopName);
          }
        }

        if (!resolvedCbUserId) {
          const onboardRes = await onboardRetailer(retailerCode);
          resolvedCbUserId = onboardRes?.data?.data?.cbUserId || null;
        }

        if (cancelled) return;

        if (!resolvedCbUserId) {
          setStatus(STATUS.ERROR);
          return;
        }

        setQrUrl(buildQrUrl(resolvedCbUserId));
        setStatus(STATUS.READY);
      } catch (err) {
        if (!cancelled) setStatus(STATUS.ERROR);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, retailerCode, knownCbUserId, shopNameProp]);

  if (!open) return null;

  return (
    <div
      className="kavach-modal-overlay"
      style={styles.overlay}
      onClick={onClose}>
      <div
        style={styles.modal}
        className="kavach-pop-in kavach-modal-card"
        onClick={(e) => e.stopPropagation()}>
        <button
          style={styles.closeBtn}
          className="kavach-pressable"
          onClick={onClose}>
          <Icon name="close" size={16} color={KAVACH_COLORS.slate} />
        </button>

        <div style={styles.title}>
          {knownCbUserId ? "View QR" : "Generate QR"}
        </div>

        {status === STATUS.LOADING && (
          <div style={styles.stateText}>Generating your QR code…</div>
        )}

        {status === STATUS.ERROR && (
          <div style={styles.stateText}>
            Couldn't generate the QR code. Please try again.
          </div>
        )}

        {status === STATUS.READY && (
          <>
            <div style={styles.qrWrap} ref={qrSvgWrapRef}>
              <QRCode value={qrUrl} size={200} />
              <button
                className="kavach-pressable"
                style={styles.downloadBtn}
                onClick={handleDownload}
                title="Download QR"
                aria-label="Download QR">
                <Icon name="download" size={18} color={KAVACH_COLORS.indigo} />
              </button>
            </div>
            <div style={styles.detailsBlock}>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Retailer Code</span>
                <span style={styles.detailValue}>{retailerCode}</span>
              </div>
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Shop Name</span>
                <span style={styles.detailValue}>{shopName || "—"}</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(10,20,35,0.55)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200,
    padding: 20,
  },
  modal: {
    position: "relative",
    width: 340,
    maxWidth: "100%",
    background: "#FFFFFF",
    borderRadius: 20,
    padding: "28px 24px 24px",
    boxShadow: "0 24px 60px rgba(10,20,35,0.35)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  closeBtn: {
    position: "absolute",
    top: 16,
    right: 16,
    width: 32,
    height: 32,
    borderRadius: "50%",
    border: "1px solid #E2E7EE",
    background: "#F7F9FC",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: KAVACH_COLORS.ink,
    marginBottom: 18,
  },
  stateText: {
    fontSize: 13.5,
    color: KAVACH_COLORS.slate,
    textAlign: "center",
    padding: "24px 8px",
  },
  qrWrap: {
    position: "relative",
    padding: 16,
    background: "#FFFFFF",
    border: "1px solid #E7EAF0",
    borderRadius: 14,
  },
  downloadBtn: {
    position: "absolute",
    bottom: 6,
    right: 6,
    width: 26,
    height: 26,
    borderRadius: "50%",
    border: "1px solid #E2E7EE",
    background: "#8abbf4",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(10,20,35,0.15)",
  },
  detailsBlock: {
    width: "100%",
    marginTop: 18,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  detailRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#F7F9FC",
    border: "1px solid #E7EAF0",
    borderRadius: 10,
    padding: "8px 12px",
  },
  detailLabel: { fontSize: 12, fontWeight: 600, color: KAVACH_COLORS.slate },
  detailValue: { fontSize: 12.5, fontWeight: 700, color: KAVACH_COLORS.ink },
};

export default KavachGenerateQrModal;
